import { useMemo } from "react"
import { IconArrowLeft } from "@tabler/icons-react"
import { useAppDispatch, useAppSelector } from "@/store"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  navDay,
  navStaffDay,
  setStaffView,
  goToday,
  setSelectedSite,
  exitStaffWeek,
  openBooking,
  closeBooking,
  openOverlap,
  closeOverlap,
  openSlotBooking,
  closeSlotBooking,
} from "@/app/booking-calendar/bookingCalendarSlice"
import { useGetCalUsersQuery } from "../bookingCalendarApi"
import { findBookingInColumn, headCell, overlapGroup } from "../lib/dayGrid"
import { longDateLabel } from "../lib/siteTime"
import { usePacificToday } from "../lib/usePacificToday"
import { columnsInView, weekdayInView } from "../lib/staffDay"
import { useStaffWeek } from "../lib/useStaffWeek"
import { bookableSlotTypes, slotTypeOf } from "../lib/slotTypes"
import { WeekView } from "../components/WeekView"
import { AppointmentDialog } from "../components/modals"
import { NewBookingDialog } from "../components/NewBookingDialog"
import { OverlapDialog } from "../components/OverlapDialog"
import { ScheduleToolbar, ScheduleLegend, ScheduleLoadingSkeleton } from "../components/ScheduleHeader"
import { StaffViewToggle } from "../components/StaffViewToggle"
import { BookingSeasonBanner } from "../components/BookingSeasonBanner"
import { CalThrottledNotice } from "@/components/CalThrottledNotice"
import { useGetBookingSeasonQuery } from "@/app/appointments/booking-season/bookingSeasonApi"

/** The site picker's value for "every site at once", which the store holds as a null site id. */
const AllSites = "all"

/**
 * One staff member's week: Monday to Friday, their appointments only.
 *
 * Two people reach this page. Somebody who takes appointments gets their own week and nothing else,
 * because what they need is when they are booked rather than the roster of the office — the team day
 * grid is the other page, behind `appointments:manage`. An administrator reaches the same week about
 * one member of the team they were looking at, by passing `calUserId`; the narrowing is the server's
 * either way, and so is the permission check.
 *
 * Somebody who works at more than one office picks between them in the sidebar. One week merged
 * across their sites is still the default, because "when am I booked" is a question about the person
 * rather than the building — but the hours, the slot length and the closures are the site's, so a
 * week that names one is the only one that can draw them.
 *
 * @param calUserId The colleague whose week this is, for an administrator. Omitted for one's own.
 */
export default function StaffWeekPage({ calUserId }: { calUserId?: number }) {
  const dispatch = useAppDispatch()
  const { selectedDate, selectedSiteId, openBookingUid, overlapBookingUids, newBookingTarget, staffView } =
    useAppSelector(state => state.bookingCalendar)
  const { permissions } = useAppSelector(state => state.permissions)

  const viewingSomeoneElse = calUserId != null

  // Only to name the person in the header — the week itself is addressed by id. The listing is
  // already in the cache, put there by the day grid this page was opened from, and skipping it on
  // one's own week keeps this page off an endpoint `appointments:view` alone cannot reach.
  const calUsersQuery = useGetCalUsersQuery(undefined, { skip: !viewingSomeoneElse })
  const staffUser = calUsersQuery.data?.find(user => user.id === calUserId)

  // The same fallback chain the day grid's columns use, so a person is called one thing on both
  // pages — and an unnamed account is still addressable rather than blank.
  const staffName = viewingSomeoneElse
    ? staffUser?.name?.trim() || staffUser?.username?.trim() || staffUser?.email?.trim()
      || `Staff #${calUserId}`
    : "You"

  // When the public may book, for the grid to mark the times they may not. Every calendar user can
  // read it. Undefined on a failed read as well as while loading: the calendar then just says
  // nothing about the season — staff are never held to it, so it is never worth blocking on.
  const seasonQuery = useGetBookingSeasonQuery()
  const bookingSeason = seasonQuery.isError ? undefined : seasonQuery.data

  const week = useStaffWeek(selectedDate, selectedSiteId, calUserId, bookingSeason)
  const {
    weekDates, sites, site, schedule, slotMinutes, timeZone, status, slotsLoading, error, resolution,
    dayUnavailability, unavailableSites, bookingCountBySite, calUserId: hostCalUserId,
  } = week

  // The office's today, Pacific, not the site's zone (AB#821): `timeZone` is what the site's
  // appointments are drawn in, and a site Cal holds in a later zone would mark tomorrow as today.
  // Kept current past midnight by `usePacificToday`.
  const today = usePacificToday()

  // AB#766: the day view is one column of the week already loaded — see `lib/staffDay`. Everything
  // else on the page (the open appointment, the overlap list) still looks across the whole week, so
  // switching between the two never loses what is open.
  const isDayView = staffView === "day"
  const shownDay = weekdayInView(selectedDate)
  const columns = useMemo(
    () => columnsInView(weekDates, staffView, selectedDate),
    [weekDates, staffView, selectedDate])

  // Booking needs one site, not all of them at once: a booking is made against a particular site's
  // event type, and with "all sites" selected the grid is a merge of several with no single slug to
  // book into. It also needs a booking permission, and a host to pin the appointment to. Which rows
  // it opens turns on the permission (AB#779): `appointments:book` takes public and private rows,
  // and managed ones are `appointments:manage`'s — the same split the API enforces.
  const bookableTypes = useMemo(() => bookableSlotTypes(permissions), [permissions])
  const canBookSlots =
    bookableTypes.length > 0 &&
    !!site?.teamSlug &&
    hostCalUserId != null

  // The slot being booked into, widened with what the dialog needs: the person's name and the site.
  const newBooking = useMemo(() => {
    if (!newBookingTarget || !site?.teamSlug) return undefined
    return { target: newBookingTarget, hostName: staffName, teamSlug: site.teamSlug }
  }, [newBookingTarget, site?.teamSlug, staffName])

  // The open appointment, found by walking the grid rather than kept in the store — the grid is
  // where a booking and the day it sits on are already joined. Includes an appointment that lost its
  // row to another, which is reachable only from the overlap list.
  const open = useMemo(() => {
    if (!openBookingUid) return undefined
    for (const day of weekDates) {
      const booking = findBookingInColumn(schedule[day.isoDate], openBookingUid)
      if (booking) return booking
    }
    return undefined
  }, [openBookingUid, weekDates, schedule])

  // The overlap list, resolved against the current grid. One cancelled from the list has left the
  // grid, so it leaves the list too. Hidden while one of them is open on top of it.
  const overlapBookings = useMemo(() => {
    if (!overlapBookingUids || openBookingUid) return undefined
    return overlapBookingUids.flatMap(uid => {
      for (const day of weekDates) {
        const booking = findBookingInColumn(schedule[day.isoDate], uid)
        if (booking) return [booking]
      }
      return []
    })
  }, [overlapBookingUids, openBookingUid, weekDates, schedule])

  const weekLabel = weekDates.length > 0
    ? `${weekDates[0]!.date} – ${weekDates.at(-1)!.date}`
    : ""
  const periodLabel = isDayView ? longDateLabel(shownDay) : weekLabel

  // Which week this is a week of. One site names itself; all of them at once is named as such rather
  // than by listing every office, which the sidebar is already doing beside it. An administrator gets
  // the person's name in front of it, because whose week this is is the thing they chose.
  const siteLabel = site?.teamName
    ?? (sites.length === 1 ? sites[0]!.teamName : sites.length > 1 ? "All sites" : "My schedule")
  const toolbarLabel = viewingSomeoneElse ? `${staffName} · ${siteLabel}` : siteLabel

  // Leaving an administrator's view of somebody else. Rendered above the empty states as well as the
  // grid: the way out of a week that has nothing in it matters more, not less.
  const backToTeamDay = viewingSomeoneElse && (
    <div className="flex items-center gap-2 border-b bg-muted/40 px-5 py-1.5">
      <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2" onClick={() => dispatch(exitStaffWeek())}>
        <IconArrowLeft className="size-4" />
        Team day
      </Button>
      <span className="text-xs text-muted-foreground">
        Viewing <span className="font-medium text-foreground">{staffName}</span>'s week
      </span>
    </div>
  )

  // Onboarding gaps, not quiet weeks. Drawn instead of the grid, because a grid of open slots would
  // say "nobody has booked you" when the truth is that nobody can.
  //
  // Worded for whichever of the two is reading. The person themself is told who to ask; the
  // administrator is told what to do, because they are the one who can do it. An administrator only
  // ever reaches the second of these — a week asked for by Cal account is already past the point
  // where the account could be missing — but both are worded rather than left to the other's copy.
  const gap = viewingSomeoneElse
    ? resolution === "noCalAccount"
      ? {
        title: `${staffName} isn't set up for appointments yet.`,
        detail: "Nothing can be booked with them until they are. Adding them to a site sets them up.",
      }
      : {
        title: `${staffName} isn't hosting anywhere yet.`,
        detail: "Appointments are booked through a site's intake event type, so nothing reaches somebody who hosts on none. Put them on a site and their week fills in.",
      }
    : resolution === "noCalAccount"
      ? {
        title: "Your account isn't set up for appointments yet.",
        detail: "Nothing can be booked with you until an administrator sets it up. Ask them to add you to a site.",
      }
      : {
        title: "You're not assigned to a site yet.",
        detail: "Appointments are booked through the site you work at, so nothing is booked to you until an administrator puts you on one.",
      }

  if (status !== "loading" && resolution !== "ok") {
    return (
      <div className="booking-calendar-main">
        {backToTeamDay}
        <div className="flex h-full flex-col items-center justify-center gap-2 py-16 text-center">
          <p className="text-sm font-medium">{gap.title}</p>
          <p className="max-w-md text-sm text-muted-foreground">{gap.detail}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="booking-calendar-layout">
      {/* ── Sidebar: the sites they work at ───────────────────────── */}
      {/* Shown even for somebody who works at one office, so the page always says which site the
          week is drawn from, in the same place for every staff member. */}
      {sites.length > 0 && (
        <aside className="booking-calendar-sidebar">
          <div className="px-3 pt-3.5 pb-4">
            <label
              htmlFor="site-select"
              className="block px-2 mb-1.5 text-[0.63rem] font-bold tracking-[0.1em] uppercase text-muted-foreground"
            >
              {viewingSomeoneElse ? "Their Site" : "My Site"}
            </label>

            {/* Radix Select values are strings, and an empty one is reserved, so "all" stands for the
                null selection. Each option carries its week's appointment count, so a site can be
                picked by where the work is — once the week is in, since a 0 before then would be a
                count of nothing. */}
            <Select
              value={site ? String(site.teamId) : AllSites}
              onValueChange={value => dispatch(setSelectedSite(value === AllSites ? null : Number(value)))}
            >
              <SelectTrigger id="site-select" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AllSites}>
                  All sites
                  {!slotsLoading && <span className="text-muted-foreground">({week.bookingCount})</span>}
                </SelectItem>
                {sites.map(s => (
                  <SelectItem key={s.teamId} value={String(s.teamId)}>
                    {s.teamName}
                    {!slotsLoading && (
                      <span className="text-muted-foreground">({bookingCountBySite[s.teamId] ?? 0})</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </aside>
      )}

      {/* ── Main content: the week ────────────────────────────────── */}
      <main className="booking-calendar-main">
        {/* The toolbar and the key pin together — see .booking-calendar-header. The way back pins
            with them: it is how an administrator leaves, and scrolling a week should not take it
            off screen. */}
        <div className="booking-calendar-header">
          {backToTeamDay}
          <ScheduleToolbar
            unit={isDayView ? "day" : "week"}
            onNavDay={(delta) => dispatch(isDayView ? navStaffDay(delta) : navDay(delta))}
            onGoToday={() => dispatch(goToday())}
            onRefresh={week.refresh}
            refreshing={week.isFetching}
            loading={slotsLoading}
            teamLabel={toolbarLabel}
            dayLabel={periodLabel}
            isToday={isDayView ? shownDay === today : weekDates.some(day => day.isoDate === today)}
            bookingCount={isDayView ? week.bookingCountByDay[shownDay] ?? 0 : week.bookingCount}
            outsideWindowCount={isDayView ? week.outsideWindowCountByDay[shownDay] ?? 0 : week.outsideWindowCount}
            awayCount={isDayView ? (dayUnavailability[shownDay] ? 1 : 0) : Object.keys(dayUnavailability).length}
            slotMinutes={slotMinutes}
            viewToggle={<StaffViewToggle view={staffView} onChange={view => dispatch(setStaffView(view))} />}
          />
          <ScheduleLegend bookable={canBookSlots} bookingSeason={!!bookingSeason} />
          <BookingSeasonBanner season={bookingSeason} />
        </div>

        {/* Cal throttling CMS is a wait, not a failure (AB#514): the busy notice, with one retry of
            its own once the wait is over, in place of the generic line. */}
        {week.throttledError ? (
          <CalThrottledNotice
            error={week.throttledError}
            onRetry={week.refresh}
            busy={week.isFetching}
            autoRetry
            className="px-5 py-2 text-xs"
          />
        ) : status === "failed" && (
          <p role="alert" className="px-5 py-2 text-xs text-red-700">{error}</p>
        )}

        {/* The grid draws no shading at all while this stands, so it is over-stating availability —
            and silence about that would be the worst of both worlds: a calendar that looks
            authoritative and is not. Named, because with a site selected the reader can see for
            themselves whether it is the week in front of them. */}
        {unavailableSites.length > 0 && status !== "loading" && (
          <p role="alert" className="px-5 py-2 text-xs text-amber-700">
            {viewingSomeoneElse ? `${staffName}'s` : "Your"} availability at{" "}
            {unavailableSites.join(", ")} could not be loaded, so slots shown as open here may not be
            bookable.
          </p>
        )}

        {status === "loading"
          ? <ScheduleLoadingSkeleton />
          : (
            <WeekView
              weekDates={columns}
              schedule={schedule}
              dayUnavailability={dayUnavailability}
              today={today}
              bookable={canBookSlots}
              bookableSlotTypes={bookableTypes}
              loading={slotsLoading}
              onSlotClick={(isoDate, slotIdx) => {
                const cell = schedule[isoDate]?.[slotIdx]
                if (!cell) return

                // Colliding appointments list them all to pick from; any other opens directly.
                if (cell.slotId) {
                  const head = headCell(schedule[isoDate], cell.slotId)
                  if (head?.hasOverlap) dispatch(openOverlap(overlapGroup(head).map(b => b.uid)))
                  else dispatch(openBooking(cell.slotId))
                  return
                }

                const slotType = slotTypeOf(cell)
                if (cell.state !== "open" || !canBookSlots || hostCalUserId == null) return
                if (!bookableTypes.includes(slotType)) return

                dispatch(openSlotBooking({
                  // One column, one person — the week's axis is days, so the host is the whole page
                  // rather than the column that was clicked.
                  columnId: "me",
                  calUserId: hostCalUserId,
                  date: isoDate,
                  startMinutes: cell.startMinutes,
                  slotType,
                }))
              }}
            />
          )}
      </main>

      <AppointmentDialog
        booking={open}
        staffName={staffName}
        timeZone={timeZone}
        onClose={() => dispatch(closeBooking())}
      />

      <OverlapDialog
        bookings={overlapBookings}
        timeZone={timeZone}
        onPick={uid => dispatch(openBooking(uid))}
        onClose={() => dispatch(closeOverlap())}
      />

      <NewBookingDialog
        target={newBooking?.target}
        hostName={newBooking?.hostName}
        teamSlug={newBooking?.teamSlug}
        timeZone={timeZone}
        slotMinutes={slotMinutes}
        bookingSeason={bookingSeason}
        onClose={() => dispatch(closeSlotBooking())}
      />
    </div>
  )
}
